import { getApiToken, getApiTokenSilent } from './tokenService';

const API_URL = process.env.REACT_APP_API_URL;

// Custom event dispatched on 401 so UserContext can trigger auth recovery
// without apiService needing React context. Only 401 (invalid/expired token)
// triggers a reset — 403 (insufficient permissions) is an authorization denial,
// not a session failure, and should not sign the user out.
export const AUTH_ERROR_EVENT = 'api:auth-error';

function checkResponse(response) {
    if (response.status === 401) {
        window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT, { detail: { status: response.status } }));
        throw new Error(`Authentication failed (${response.status})`);
    }
    if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
    }
}

async function authHeaders(contentType = 'application/json') {
    const token = await getApiToken();
    const headers = { 'Authorization': `Bearer ${token}` };
    if (contentType) headers['Content-Type'] = contentType;
    return headers;
}

export async function fetchData() {
    const response = await fetch(`${API_URL}/api/data`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    checkResponse(response);
    return response.json();
}

export async function fetchAssignedData(id) {
    const response = await fetch(`${API_URL}/api/assigneddata?id=${id}`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    checkResponse(response);
    return response.json();
}

export async function fetchUsers() {
    const response = await fetch(`${API_URL}/api/users`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    checkResponse(response);
    return response.json();
}

export async function createIssue(issueData, attachments = []) {
    const formData = new FormData();
    formData.append('issueData', JSON.stringify(issueData));
    if (Array.isArray(attachments)) {
        attachments.forEach((attachment) => {
            formData.append('attachments', attachment);
        });
    }
    const response = await fetch(`${API_URL}/api/issue`, {
        method: 'POST',
        headers: await authHeaders(null),
        body: formData,
    });
    checkResponse(response);
    return response.json();
}

export async function updateIssue(updateData, deletedAttachments = [], newAttachments = [], previousAssignedUsers = []) {
    const formData = new FormData();
    formData.append('updateData', JSON.stringify(updateData));
    formData.append('previousAssignedUsers', JSON.stringify(previousAssignedUsers));
    formData.append('deletedAttachments', Array.isArray(deletedAttachments)
        ? JSON.stringify(deletedAttachments)
        : JSON.stringify([]));
    if (Array.isArray(newAttachments)) {
        newAttachments.forEach((attachment) => {
            formData.append('newAttachments', attachment);
        });
    }
    const response = await fetch(`${API_URL}/api/update`, {
        method: 'PATCH',
        headers: await authHeaders(null),
        body: formData,
    });
    checkResponse(response);
    return response.json();
}

export async function deleteIssue(issue) {
    const response = await fetch(`${API_URL}/api/delete`, {
        method: 'DELETE',
        headers: await authHeaders(),
        body: JSON.stringify(issue),
    });
    checkResponse(response);
    if (response.status === 204) return { message: 'Issue successfully deleted' };
    return response.json();
}

export async function getPreferences(id) {
    const response = await fetch(`${API_URL}/api/preferences?id=${encodeURIComponent(id)}`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    checkResponse(response);
    return response.json();
}

export async function setPreferences(id, preferences) {
    const response = await fetch(`${API_URL}/api/setpreferences`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ id, preferences }),
    });
    checkResponse(response);
    return response.json();
}

export async function getSasToken() {
    try {
        const response = await fetch(`${API_URL}/api/getSasToken`, {
            method: 'POST',
            headers: await authHeaders(),
        });
        checkResponse(response);
        const data = await response.json();
        return { sasToken: data.sasToken, expiresOn: data.expiresOn };
    } catch (error) {
        console.error('Error fetching SAS token:', error);
        throw error;
    }
}

export async function getSasTokenSilent() {
    try {
        const token = await getApiTokenSilent();
        const response = await fetch(`${API_URL}/api/getSasToken`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });
        checkResponse(response);
        const data = await response.json();
        return { sasToken: data.sasToken, expiresOn: data.expiresOn };
    } catch (error) {
        console.error('Error fetching SAS token (silent):', error);
        throw error;
    }
}
